import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, Save, Plus, Trash2, Pencil, Eye } from 'lucide-react';
import { AnecdotePhotoUpload } from './AnecdotePhotoUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Render a single field (same semantics as the contributor form).
 * The `field` object comes from the book template.
 */
const FieldRenderer = ({ field, value, onChange, disabled, bookId }) => {
  // The authorization field must never be editable from the editor —
  // only the contributor themselves (via their invite link) can change it.
  const isLocked = field.id === 'authorization';
  if (disabled || isLocked) {
    const display = formatDisplayValue(field, value);
    return (
      <div className="text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 min-h-[40px] whitespace-pre-wrap">
        {display || <span className="text-slate-400 italic">— sin datos —</span>}
        {isLocked && !disabled && (
          <div className="mt-1 text-[11px] text-slate-500 italic">
            Solo el colaborador puede modificar este campo desde su enlace de invitación.
          </div>
        )}
      </div>
    );
  }
  switch (field.type) {
    case 'text':
    case 'email':
    case 'tel':
    case 'date':
      return (
        <Input
          type={field.type}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          data-testid={`edit-field-${field.id}`}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      );
    case 'textarea':
      return (
        <Textarea
          rows={field.rows || 4}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          data-testid={`edit-field-${field.id}`}
        />
      );
    case 'select':
      return (
        <Select value={value ?? ''} onValueChange={onChange}>
          <SelectTrigger><SelectValue placeholder={field.placeholder || 'Elige'} /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((o) => (
              <SelectItem key={o.value || o} value={o.value || o}>
                {o.label || o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'checkbox':
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          {field.placeholder || field.label}
        </label>
      );
    case 'photos':
      return (
        <AnecdotePhotoUpload
          photos={Array.isArray(value) ? value : []}
          onChange={onChange}
          maxPhotos={field.max || 2}
          bookId={bookId}
          context="contribution_edit"
        />
      );
    default:
      return (
        <Input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      );
  }
};

const formatDisplayValue = (field, value) => {
  if (value === undefined || value === null || value === '') return '';
  if (field.type === 'checkbox') return value ? 'Sí' : 'No';
  if (field.type === 'photos' && Array.isArray(value)) {
    if (!value.length) return '';
    return `${value.length} foto${value.length > 1 ? 's' : ''} adjunta${value.length > 1 ? 's' : ''}`;
  }
  if (field.type === 'select' && field.options) {
    const opt = (field.options || []).find((o) => (o.value || o) === value);
    return opt ? (opt.label || opt) : value;
  }
  if (field.type === 'radio' && field.options) {
    const opt = (field.options || []).find((o) => (o.value || o) === value);
    return opt ? (opt.label || opt) : value;
  }
  return String(value);
};

/**
 * ContributionEditor: view and optionally edit a contributor's submitted form.
 * Accessible from book owner and admin.
 */
const ContributionEditor = ({ open, onClose, contributionId, initialMode = 'view' }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(initialMode);
  const [data, setData] = useState(null); // { contribution_id, contributor_name, form_template, form_data }

  const load = useCallback(async () => {
    if (!contributionId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/contributions/${contributionId}/full`, {
        withCredentials: true,
      });
      setData(data);
    } catch (e) {
      toast.error('No se pudo cargar la aportación');
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [contributionId, onClose]);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      load();
    }
  }, [open, initialMode, load]);

  const updateField = (sectionId, fieldId, val) => {
    setData((d) => ({
      ...d,
      form_data: {
        ...d.form_data,
        [sectionId]: { ...(d.form_data?.[sectionId] || {}), [fieldId]: val },
      },
    }));
  };

  const updateRepeatField = (sectionId, idx, fieldId, val) => {
    setData((d) => {
      const arr = Array.isArray(d.form_data?.[sectionId]) ? [...d.form_data[sectionId]] : [];
      arr[idx] = { ...(arr[idx] || {}), [fieldId]: val };
      return { ...d, form_data: { ...d.form_data, [sectionId]: arr } };
    });
  };

  const addRepeatItem = (sectionId) => {
    setData((d) => {
      const arr = Array.isArray(d.form_data?.[sectionId]) ? [...d.form_data[sectionId]] : [];
      arr.push({});
      return { ...d, form_data: { ...d.form_data, [sectionId]: arr } };
    });
  };

  const removeRepeatItem = (sectionId, idx) => {
    setData((d) => {
      const arr = Array.isArray(d.form_data?.[sectionId]) ? [...d.form_data[sectionId]] : [];
      arr.splice(idx, 1);
      return { ...d, form_data: { ...d.form_data, [sectionId]: arr } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/contributions/${contributionId}`,
        { form_data: data.form_data },
        { withCredentials: true }
      );
      toast.success('Aportación actualizada');
      setMode('view');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const isEdit = mode === 'edit';
  const template = data?.form_template || {};
  const sections = template.sections || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="contribution-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-8">
            <span>
              {isEdit ? 'Editar aportación' : 'Aportación'}
              {data?.contributor_name && <span className="text-slate-500 font-normal"> · {data.contributor_name}</span>}
            </span>
            {!loading && data && (
              isEdit ? null : (
                <Button size="sm" variant="outline" onClick={() => setMode('edit')} data-testid="contribution-edit-mode" className="gap-1">
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </Button>
              )
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : !data ? (
          <div className="py-12 text-center text-slate-500">No se pudo cargar la aportación.</div>
        ) : (
          <div className="space-y-6">
            {sections.map((section) => (
              <section key={section.id} className="border border-slate-200 rounded-xl p-4 bg-white">
                <header className="mb-3 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-800">{section.label || section.title || section.id}</h3>
                    {section.description && <p className="text-xs text-slate-500">{section.description}</p>}
                  </div>
                  {isEdit && section.repeatable && (
                    <Button size="sm" variant="outline" onClick={() => addRepeatItem(section.id)} className="gap-1">
                      <Plus className="w-3.5 h-3.5" /> Añadir
                    </Button>
                  )}
                </header>

                {section.repeatable ? (
                  <div className="space-y-3">
                    {(data.form_data?.[section.id] || []).length === 0 && !isEdit && (
                      <div className="text-xs text-slate-400 italic">— sin entradas —</div>
                    )}
                    {(data.form_data?.[section.id] || []).map((item, idx) => (
                      <div key={idx} className="bg-slate-50 border border-slate-100 rounded-lg p-3 relative">
                        {isEdit && (
                          <button
                            type="button"
                            onClick={() => removeRepeatItem(section.id, idx)}
                            className="absolute top-2 right-2 text-rose-500 hover:text-rose-700"
                            data-testid={`remove-item-${section.id}-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {(section.fields || []).map((field) => (
                            <div key={field.id} className={field.type === 'textarea' || field.type === 'photos' ? 'md:col-span-2' : ''}>
                              <Label className="text-xs mb-1 block">
                                {field.label}
                                {field.required && isEdit && <span className="text-red-500 ml-0.5">*</span>}
                              </Label>
                              <FieldRenderer
                                field={field}
                                value={item?.[field.id]}
                                onChange={(v) => updateRepeatField(section.id, idx, field.id, v)}
                                disabled={!isEdit}
                                bookId={data.book_id}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(section.fields || []).map((field) => (
                      <div key={field.id} className={field.type === 'textarea' || field.type === 'photos' ? 'md:col-span-2' : ''}>
                        <Label className="text-xs mb-1 block">
                          {field.label}
                          {field.required && isEdit && <span className="text-red-500 ml-0.5">*</span>}
                        </Label>
                        <FieldRenderer
                          field={field}
                          value={(data.form_data?.[section.id] || {})[field.id]}
                          onChange={(v) => updateField(section.id, field.id, v)}
                          disabled={!isEdit}
                          bookId={data.book_id}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {isEdit ? 'Cancelar' : 'Cerrar'}
          </Button>
          {isEdit && (
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="contribution-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar cambios
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContributionEditor;
export { ContributionEditor };
